import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import {
  Box,
  Typography,
  Paper,
  Grid,
  Card,
  CardContent,
  Button,
  CircularProgress,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Tabs,
  Tab,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Checkbox,
  Tooltip,
  Avatar
} from '@mui/material';
import {
  Assignment as AssignmentIcon,
  Save as SaveIcon,
  Edit as EditIcon,
  Cancel as CancelIcon,
  ArrowBack as ArrowBackIcon,
  Person as PersonIcon,
  Class as ClassIcon
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useSnackbar } from 'notistack';
import { fetchJson } from '../../../utils/fetchJson';
import { usePhase } from '../../../contexts/PhaseContext';
import useScopeAutoRefresh from '../../../hooks/useScopeAutoRefresh';

const normalizePhaseCodeValue = (value) => {
  if (value === null || value === undefined) return '';
  const str = value.toString().trim().toLowerCase();
  if (!str) return '';
  if (['nur_pri', 'nursery', 'nursery_primary', 'primary', 'nur', 'pre_school', 'basic'].includes(str)) return 'nur_pri';
  if (['sec', 'secondary', 'second_cycle', 'first_cycle', 'college', 'lycee', 'lycée', 'high_school'].includes(str)) return 'sec';
  return str;
};

const extractPhaseIdFromRecord = (record = {}) => {
  const candidates = [
    record.phaseId,
    record.phaseID,
    record.phase_id,
    record.PhaseId,
    record.PhaseID,
    record.educationPhaseId,
    record.education_phase_id,
    record.phaseIdFk,
    record.phase_id_fk,
    record.phaseId_fk
  ];
  for (const candidate of candidates) {
    const parsed = Number(candidate);
    if (Number.isFinite(parsed)) return parsed;
  }
  return null;
};

const extractPhaseCodeFromRecord = (record = {}) => {
  const candidates = [
    record.phaseCode,
    record.phase_code,
    record.PhaseCode,
    record.phase,
    record.Phase,
    record.phaseName,
    record.phase_name,
    record.PhaseName,
    record.phaseSlug,
    record.phase_slug,
    record.cycleType,
    record.CycleType,
    record.educationPhase,
    record.education_phase,
    record.educationPhaseCode,
    record.education_phase_code,
    record.level,
    record.Level,
    record.levelName,
    record.level_name,
    record.sectionPhase,
    record.section_phase
  ];
  for (const candidate of candidates) {
    const normalized = normalizePhaseCodeValue(candidate);
    if (normalized) return normalized;
  }
  const fallbackId = extractPhaseIdFromRecord(record);
  if (fallbackId === 1) return 'nur_pri';
  if (fallbackId === 2) return 'sec';
  return '';
};

const ClassAssignmentsPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  
  const { activePhase, activePhaseId } = usePhase();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [classes, setClasses] = useState([]);
  const [teachers, setTeachers] = useState([]);
  const [classSubjects, setClassSubjects] = useState({});
  const [teacherSubjects, setTeacherSubjects] = useState({});
  const [subjects, setSubjects] = useState([]);
  const [error, setError] = useState(null);
  const [subjError, setSubjError] = useState('');
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingClass, setEditingClass] = useState(null);
  const [editFormData, setEditFormData] = useState({
    classMasterId: '',
    notes: ''
  });
  const [compatibleTeachers, setCompatibleTeachers] = useState([]);
  const [namesLoaded, setNamesLoaded] = useState(false);
  const [compatibleLoading, setCompatibleLoading] = useState(false);
  const [activeTab, setActiveTab] = useState(0);
  const [editSubjectsOpen, setEditSubjectsOpen] = useState(false);
  const [subjectSelection, setSubjectSelection] = useState([]);
  const [subjectSearch, setSubjectSearch] = useState('');
  const [subjectsLoading, setSubjectsLoading] = useState(false);
  const [subjectsSaving, setSubjectsSaving] = useState(false);
  const [subjectsByClass, setSubjectsByClass] = useState({}); // classId -> [{id,name,code}]
  const [subjectNamesById, setSubjectNamesById] = useState({}); // subjectId -> name
  const [electClassId, setElectClassId] = useState('');
  const [electStudents, setElectStudents] = useState([]);
  const [electSubjects, setElectSubjects] = useState([]);
  const [electMap, setElectMap] = useState({});
  const [electOriginalMap, setElectOriginalMap] = useState({});
  const [electLoading, setElectLoading] = useState(false);
  const [electStudentId, setElectStudentId] = useState('');

  const resolvedPhaseCode = useMemo(() => {
    const fromCtx = activePhase ?? localStorage.getItem('schoolAdmin_activePhase') ?? localStorage.getItem('activePhase');
    return normalizePhaseCodeValue(fromCtx);
  }, [activePhase]);

  const isSecPhase = resolvedPhaseCode === 'sec';
  const isNurPriPhase = resolvedPhaseCode === 'nur_pri';
  const classMasterLabel = isSecPhase
    ? t('classManagement.assignments.labels.classMaster', { defaultValue: 'Class Master' })
    : t('classManagement.assignments.labels.classTeacher', { defaultValue: 'Class Teacher' });
  const classMastersTabLabel = isSecPhase
    ? t('classManagement.assignments.labels.classMasters', { defaultValue: 'Class Masters' })
    : t('classManagement.assignments.labels.classTeachers', { defaultValue: 'Class Teachers' });
  const currentClassMasterLabel = isSecPhase
    ? t('classManagement.assignments.labels.currentClassMaster', { defaultValue: 'Current Class Master' })
    : t('classManagement.assignments.labels.currentClassTeacher', { defaultValue: 'Current Class Teacher' });
  const assignClassMasterLabel = isSecPhase
    ? t('classManagement.assignments.labels.assignClassMaster', { defaultValue: 'Assign Class Master' })
    : t('classManagement.assignments.labels.assignClassTeacher', { defaultValue: 'Assign Class Teacher' });
  const selectClassMasterLabel = isSecPhase
    ? t('classManagement.assignments.labels.selectClassMaster', { defaultValue: 'Select Class Master' })
    : t('classManagement.assignments.labels.selectClassTeacher', { defaultValue: 'Select Class Teacher' });
  const classMasterSuffixLabel = isSecPhase
    ? t('classManagement.assignments.labels.classMasterSuffix', { defaultValue: '(Class Master)' })
    : t('classManagement.assignments.labels.classTeacherSuffix', { defaultValue: '(Class Teacher)' });

  const assignmentsSubtitle = isSecPhase
    ? t('classManagement.assignments.subtitle')
    : (isNurPriPhase
        ? t('classManagement.assignments.subtitleClassTeacher', { defaultValue: 'Assign and manage Class Teachers and Subjects for all classes' })
        : t('classManagement.assignments.subtitleClassTeacher', { defaultValue: 'Assign and manage Class Teachers and Subjects for all classes' }));

  const editAssignmentTitle = isSecPhase
    ? t('classManagement.assignments.dialogs.editAssignment.title')
    : t('classManagement.assignments.dialogs.editAssignment.titleClassTeacher', { defaultValue: 'Edit Class Teacher Assignment' });

  const editAssignmentInfo = isSecPhase
    ? t('classManagement.assignments.dialogs.editAssignment.info')
    : t('classManagement.assignments.dialogs.editAssignment.infoClassTeacher', { defaultValue: 'Only teachers who teach subjects in this class can be assigned as Class Teacher.' });

  const assignmentConflictToast = isSecPhase
    ? t('classManagement.assignments.toasts.assignment.conflict')
    : t('classManagement.assignments.toasts.assignment.conflictClassTeacher', { defaultValue: 'Selected teacher is already a Class Teacher of another class' });
  const assignmentSuccessToast = isSecPhase
    ? t('classManagement.assignments.toasts.assignment.success')
    : t('classManagement.assignments.toasts.assignment.successClassTeacher', { defaultValue: 'Class Teacher assignment updated successfully!' });

  const resolvedPhaseId = useMemo(() => {
    const fromCtx = activePhaseId || localStorage.getItem('schoolAdmin_activePhaseId') || localStorage.getItem('activePhaseId');
    const parsed = parseInt(fromCtx, 10);
    return Number.isFinite(parsed) ? parsed : null;
  }, [activePhaseId]);

  const LEGACY_DEFAULT_PHASE_CODE = 'nur_pri';

  const filterRecordsByPhase = useCallback((records) => {
    if (!Array.isArray(records)) return Array.isArray(records) ? records : [];
    if (!resolvedPhaseCode && !resolvedPhaseId) return records;
    return records.filter((record) => {
      if (!record) return false;
      const recPhaseId = extractPhaseIdFromRecord(record);
      if (resolvedPhaseId && recPhaseId) {
        return recPhaseId === resolvedPhaseId;
      }
      const recPhaseCode =
        extractPhaseCodeFromRecord(record) ||
        normalizePhaseCodeValue(record?.__phaseHint) ||
        LEGACY_DEFAULT_PHASE_CODE;
      if (resolvedPhaseCode && recPhaseCode) {
        return recPhaseCode === resolvedPhaseCode;
      }
      // Drop records whose phase cannot be confirmed to prevent cross-phase bleed.
      return false;
    });
  }, [resolvedPhaseCode, resolvedPhaseId]);
  // Persist debug logs to localStorage so they survive page reloads
  const _initialLastLoad = (() => { try { const raw = localStorage.getItem('classAssignments:lastLoadLog'); return raw ? JSON.parse(raw) : null; } catch (_) { return null; } })();
  const _initialLastSave = (() => { try { const raw = localStorage.getItem('classAssignments:lastSaveLog'); return raw ? JSON.parse(raw) : null; } catch (_) { return null; } })();
  const [lastSaveLog, setLastSaveLogState] = useState(_initialLastSave);
  const [lastLoadLog, setLastLoadLogState] = useState(_initialLastLoad);
  const setLastSaveLog = (v) => { try { localStorage.setItem('classAssignments:lastSaveLog', JSON.stringify(v)); } catch(_) {} setLastSaveLogState(v); };
  const setLastLoadLog = (v) => { try { localStorage.setItem('classAssignments:lastLoadLog', JSON.stringify(v)); } catch(_) {} setLastLoadLogState(v); };

  // Small local debug timeline persisted to localStorage for troubleshooting
  const pushDebugTimeline = (event) => {
    try {
      const key = 'classAssignments:debugTimeline';
      const raw = localStorage.getItem(key);
      const arr = raw ? JSON.parse(raw) : [];
      arr.push({ time: new Date().toISOString(), ...event });
      const sliced = arr.slice(-60);
      localStorage.setItem(key, JSON.stringify(sliced));
    } catch (_) {}
  };

  useEffect(() => {
    if (activeTab === 2 && false && electClassId) loadElectives(electClassId);
  }, [activeTab, electClassId]);

  const loadElectives = async (classId) => {
    try {
      setElectLoading(true);
      const schoolId = localStorage.getItem('schoolId') || localStorage.getItem('activeSchoolId') || localStorage.getItem('scopeSchoolId') || '';
      const phaseCode = resolvedPhaseCode;
      const ayId = await resolveCurrentAyId(schoolId, phaseCode);
      const phaseId = localStorage.getItem('schoolAdmin_activePhaseId') || localStorage.getItem('activePhaseId') || '';
      const headers = schoolId ? { 'X-Scope-School-Id': schoolId, 'X-Active-Phase': phaseCode, 'X-Academic-Year-Id': ayId, 'X-Active-Phase-Id': phaseId } : { 'X-Active-Phase': phaseCode, 'X-Academic-Year-Id': ayId, 'X-Active-Phase-Id': phaseId };
      const q = `?academicYearId=${encodeURIComponent(ayId)}&phase=${encodeURIComponent(phaseCode)}&schoolId=${encodeURIComponent(schoolId)}`;
      const [stu, offered, enroll] = await Promise.all([
        fetchJson(`/api/allocations/students/class/${encodeURIComponent(classId)}`, { headers }).catch(() => ({ students: [] })),
        fetchJson(`/api/class-subjects/class/${encodeURIComponent(classId)}${q}`, { headers }).catch(() => []),
        fetchJson(`/api/allocations/student-subjects/class/${encodeURIComponent(classId)}${q}`, { headers }).catch(() => ({ map: {} }))
      ]);
      const students = Array.isArray(stu?.students) ? stu.students : [];
      let subsRaw = Array.isArray(offered) ? offered : (offered?.classSubjects || offered?.data || []);
      if (!Array.isArray(subsRaw)) subsRaw = [];
      const subs = subsRaw.map(it => {
        const s = (it.Subject || it.subject || {});
        return {
          id: s.id ?? it.subjectId ?? it.code,
          name: s.name || it.name || it.code || t('classManagement.assignments.subjectFallback', { id: s.id ?? it.subjectId ?? '' }),
          code: s.code || it.code || ''
        };
      }).filter(x => x && x.id);
      const seen = new Set();
      const uniqueSubs = [];
      for (const s of subs) { const k = String(s.id); if (!seen.has(k)) { seen.add(k); uniqueSubs.push(s); } }
      const map = (enroll && enroll.map) ? enroll.map : {};
      setElectStudents(students);
      setElectSubjects(uniqueSubs);
      setElectMap(map || {});
      setElectOriginalMap(map || {});
      if (!electStudentId && students && students.length) setElectStudentId(String(students[0].id));
    } catch (e) {
      setElectStudents([]);
      setElectSubjects([]);
      setElectMap({});
    } finally {
      setElectLoading(false);
    }
  };

  const toggleElective = async (studentId, subjectId, checked) => {
    const sid = String(studentId);
    const prev = electMap[sid] || [];
    const next = checked ? Array.from(new Set([...(prev || []), subjectId])) : (prev || []).filter(x => String(x) !== String(subjectId));
    setElectMap(prevMap => ({ ...prevMap, [sid]: next }));
  };

  const saveElectivesForStudent = async () => {
    if (!electClassId || !electStudentId) return;
    const sid = String(electStudentId);
    const before = Array.from(new Set((electOriginalMap[sid] || []).map(String)));
    const after = Array.from(new Set((electMap[sid] || []).map(String)));
    const toAdd = after.filter(x => !before.includes(x)).map(x => ({ classId: Number(electClassId), studentId: Number(sid), subjectId: Number(x) }));
    const toRemove = before.filter(x => !after.includes(x)).map(x => ({ studentId: Number(sid), subjectId: Number(x) }));
    if (toAdd.length === 0 && toRemove.length === 0) {
      enqueueSnackbar(t('classManagement.assignments.electives.toasts.noChanges'), { variant: 'info' });
      return;
    }
    try {
      const schoolId = localStorage.getItem('schoolId') || localStorage.getItem('activeSchoolId') || localStorage.getItem('scopeSchoolId') || '';
      const phaseCode = (activePhase || localStorage.getItem('schoolAdmin_activePhase') || localStorage.getItem('activePhase') || '');
      const ayId = await resolveCurrentAyId(schoolId, phaseCode);
      const phaseId = localStorage.getItem('schoolAdmin_activePhaseId') || localStorage.getItem('activePhaseId') || '';
      const headers = { 'Content-Type': 'application/json', ...(schoolId ? { 'X-Scope-School-Id': schoolId } : {}), 'X-Active-Phase': phaseCode, 'X-Academic-Year-Id': ayId, 'X-Active-Phase-Id': phaseId };
      if (toAdd.length) await fetchJson('/api/allocations/student-subjects/bulk', { method: 'POST', headers, body: JSON.stringify(toAdd) });
      if (toRemove.length) await fetchJson('/api/allocations/student-subjects/bulk', { method: 'DELETE', headers, body: JSON.stringify({ classId: Number(electClassId), pairs: toRemove }) });
      enqueueSnackbar(t('classManagement.assignments.electives.toasts.saveSuccess'), { variant: 'success' });
      // Refresh originals to current
      setElectOriginalMap(prev => ({ ...prev, [sid]: [...(electMap[sid] || [])] }));
    } catch (err) {
      enqueueSnackbar(t('classManagement.assignments.electives.toasts.saveError'), { variant: 'error' });
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const reqSeqRef = useRef(0);
  const prevCtxRef = useRef('');
  const fetchData = async () => {
    const mySeq = ++reqSeqRef.current;
    try {
      setError(null);
      const schoolId = localStorage.getItem('schoolId') || localStorage.getItem('activeSchoolId') || localStorage.getItem('scopeSchoolId') || '';
      const phaseCode = (activePhase || localStorage.getItem('schoolAdmin_activePhase') || localStorage.getItem('activePhase') || '');
      // Resolve the Global Academic Year ID expected by backend middlewares (use phase-aware resolver)
      const ayId = await resolveCurrentAyId(schoolId, phaseCode);
      const ayKey = schoolId ? `${schoolId}:currentAcademicYearId` : 'currentAcademicYearId';

      const ctxKey = `${schoolId}|${phaseCode || '-' }|${ayId || '-'}`;
      const cacheKey = `classAssignments:list:${ctxKey}`;

      // Try to hydrate per-AY/phase class-subjects map so subjects show immediately after reload
      try {
        const csCacheKey = `classAssignments:cs:${schoolId}|${phaseCode || '-'}|${ayId || '-'}`;
        const csRaw = localStorage.getItem(csCacheKey);
        if (csRaw) {
          const csObj = JSON.parse(csRaw);
          if (csObj && typeof csObj === 'object') {
            setClassSubjects(csObj);
            pushDebugTimeline({ stage: 'fetchData:hydratedCsCache', csCacheKey, ctxKey });
          }
        }
      } catch (_) {}

      if (prevCtxRef.current !== ctxKey) {
        prevCtxRef.current = ctxKey;
        if (!localStorage.getItem(cacheKey)) setClasses([]);
      }
      try {
        const raw = localStorage.getItem(cacheKey);
        if (raw) {
          const cached = JSON.parse(raw);
          if (Array.isArray(cached) && reqSeqRef.current === mySeq) setClasses(cached);
        }
      } catch (_) {}

      // Helper: fast-fail timeout wrapper
      const withTimeout = (promise, ms = 3000) => Promise.race([
        promise,
        new Promise((_, rej) => setTimeout(() => rej(new Error('Timeout')), ms))
      ]);

      const headers = schoolId
        ? { 'X-Scope-School-Id': schoolId, 'X-Active-Phase': phaseCode, 'X-Active-Phase-Id': resolvedPhaseId ?? undefined, 'X-Academic-Year-Id': ayId }
        : { 'X-Active-Phase': phaseCode, 'X-Active-Phase-Id': resolvedPhaseId ?? undefined, 'X-Academic-Year-Id': ayId };
      try { pushDebugTimeline({ stage: 'fetchData:initialFetch', ctxKey, headersUsed: headers }); } catch(_){}
      const qPhase = `${phaseCode ? `?phase=${encodeURIComponent(phaseCode)}` : ''}${(phaseCode ? '&' : '?')}schoolId=${encodeURIComponent(schoolId)}`;
      const [p1, p2, p3] = await Promise.allSettled([
        withTimeout(fetchJson(`/api/classes-lite${qPhase}`, { headers }), 3000),
        // Use listUsers with large pageSize to obtain full staff list; will be filtered/normalized below
        withTimeout(fetchJson(`/api/users?pageSize=500${phaseCode ? `&phase=${encodeURIComponent(phaseCode)}` : ''}${schoolId ? `&schoolId=${encodeURIComponent(schoolId)}` : ''}`, { headers }), 3000),
        withTimeout(fetchJson('/api/global-academic-years', { headers }), 3000),
      ]);

      const classesLite = p1 && p1.status === 'fulfilled' ? p1.value : { classes: [] };
      const teachersData = p2 && p2.status === 'fulfilled' ? p2.value : [];
      const yearsData = p3 && p3.status === 'fulfilled' ? p3.value : [];

      let items = Array.isArray(classesLite?.classes) ? classesLite.classes : [];
      // Phase-scoped fallbacks if empty
      if ((!items || items.length === 0) && schoolId) {
        // 1) Fast allocations endpoint (phase-aware by headers/query)
        try {
          const alloc = await withTimeout(fetchJson(`/api/allocations/classes${qPhase}`, { headers }), 3000);
          const arr = Array.isArray(alloc) ? alloc : (alloc?.classes || alloc?.data || []);
          if (Array.isArray(arr) && arr.length > 0) {
            items = arr.map(it => ({ id: it.id, name: it.name }));
          }
        } catch (_) {}
      }
      if ((!items || items.length === 0) && schoolId) {
        // 2) Recent-top (phase-aware via query/header)
        try {
          const recent = await withTimeout(fetchJson(`/api/classes/recent-top${qPhase}`, { headers }), 3000);
          const arr = Array.isArray(recent?.classes) ? recent.classes : (Array.isArray(recent) ? recent : []);
          if (Array.isArray(arr) && arr.length > 0) items = arr;
        } catch (_) {}
      }
      if ((!items || items.length === 0) && schoolId && !phaseCode) {
        // 3) anyPhase as a last resort ONLY when no explicit phase is active
        try {
          const alt = await withTimeout(fetchJson(`/api/classes-lite?anyPhase=1&schoolId=${encodeURIComponent(schoolId)}`, { headers }), 2500);
          if (Array.isArray(alt?.classes) && alt.classes.length > 0) items = alt.classes;
        } catch (_) {}
      }
      // Normalize teachers list with robust fallbacks
      let teachersArray = Array.isArray(teachersData)
        ? teachersData
        : (teachersData?.rows || teachersData?.users || teachersData?.data || []);
      if (!teachersArray || teachersArray.length === 0) {
        try {
          const t1 = await withTimeout(fetchJson('/api/teachers', { headers }), 3000);
          const arr = Array.isArray(t1) ? t1 : (t1?.teachers || t1?.rows || t1?.data || []);
          if (Array.isArray(arr) && arr.length) teachersArray = arr;
        } catch (_) {}
      }
      if (!teachersArray || teachersArray.length === 0) {
        try {
          const t2 = await withTimeout(fetchJson('/teachers', { headers }), 3000);
          const arr = Array.isArray(t2) ? t2 : (t2?.teachers || t2?.rows || t2?.data || []);
          if (Array.isArray(arr) && arr.length) teachersArray = arr;
        } catch (_) {}
      }
      if (!teachersArray || teachersArray.length === 0) {
        try {
          const t3 = await withTimeout(fetchJson('/api/staff/faculty', { headers }), 3000);
          const arr = Array.isArray(t3) ? t3 : (t3?.faculty || t3?.staff || t3?.data || []);
          if (Array.isArray(arr) && arr.length) teachersArray = arr;
        } catch (_) {}
      }
      // Normalize teacher structure (preserve role so we can filter only teachers in Teacher's Subjects tab)
      teachersArray = (teachersArray || []).map(t => ({
        id: t.id ?? t.userId ?? t.staffId ?? t.teacherId ?? t.accountId,
        firstName: t.firstName ?? t.givenName ?? (t.name?.split(' ')[0]) ?? '',
        lastName: t.lastName ?? t.surname ?? (t.name?.split(' ').slice(1).join(' ')) ?? '',
        email: t.email || t.workEmail || '',
        avatarUrl: t.avatarUrl || null,
        name: t.name || '',
        role: t.role || t.roleName || (t.Role && t.Role.name) || null,
      })).filter(t => t.id != null);
      // subject arrays will be filled by deferred effect below
      const classSubjectsArray = [];
      const teacherSubjectsArray = [];
      const yearsList = Array.isArray(yearsData?.years) ? yearsData.years : (Array.isArray(yearsData) ? yearsData : []);
      const ayLabel = (yearsList.find(y => String(y.id) === String(ayId))?.name) || (yearsList.find(y => String(y.id) === String(ayId))?.label) || '';

      const classSubjectsMap = {};
      classSubjectsArray.forEach(cs => {
        if (!classSubjectsMap[cs.classId]) classSubjectsMap[cs.classId] = [];
        classSubjectsMap[cs.classId].push(cs.subjectId);
      });

      const teacherSubjectsMap = {};
      teacherSubjectsArray.forEach(ts => {
        if (!teacherSubjectsMap[ts.teacherId]) teacherSubjectsMap[ts.teacherId] = [];
        teacherSubjectsMap[ts.teacherId].push(ts.subjectId);
      });

      const filteredItems = filterRecordsByPhase(items);

      const rows = filteredItems.map(it => ({
        id: it.id,
        name: it.name,
        level: it.level || it.classLevel || it.Level || null,
        phaseId: extractPhaseIdFromRecord(it),
        classMasterId: it.classMasterId || null, // may be null if not included
        academicYear: ayLabel || '—',
        assignmentNotes: it.assignmentNotes || '',
        assignmentDate: it.assignmentDate || null,
      }));

      if (reqSeqRef.current === mySeq) {
        setClasses(rows);
        setTeachers(teachersArray);
        setClassSubjects(classSubjectsMap);
        setTeacherSubjects(teacherSubjectsMap);
        try { localStorage.setItem(cacheKey, JSON.stringify(rows)); } catch (_) {}
        try {
          // small debug entry noting what we set in memory after the initial fetch
          pushDebugTimeline({ stage: 'fetchData:setState', ctxKey, classes: (rows || []).length, csKeys: Object.keys(classSubjectsMap || {}).length });
          console.debug('[ClassAssignments] setState', { ctxKey, classes: (rows || []).length, csKeys: Object.keys(classSubjectsMap || {}).length });
        } catch (_) {}
      }

    } catch (error) {
      console.error('Error fetching data:', error);
      setError(t('classManagement.assignments.errors.fetchData', { message: error?.message || '' }));
    } finally {
      if (reqSeqRef.current === mySeq) setLoading(false);
    }
  };

  // Resolve the Global Academic Year ID expected by backend middlewares
  // Resolve the Global Academic Year ID expected by backend middlewares
  // Accepts schoolId and optional phaseCode so the backend can return a school/phase-scoped current AY
  const resolveCurrentAyId = async (schoolId, phaseCode = '') => {
    // Try explicit localStorage for global AY id
    const lsGlobal = localStorage.getItem('currentGlobalAcademicYearId') || (schoolId ? localStorage.getItem(`${schoolId}:currentGlobalAcademicYearId`) : null);
    if (lsGlobal) return lsGlobal;
    // Fall back to global list using headers so school-scoped endpoints can return correct AY
    try {
      const headers = schoolId ? { 'X-Scope-School-Id': schoolId, 'X-Active-Phase': (phaseCode || '') } : (phaseCode ? { 'X-Active-Phase': phaseCode } : {});
      const years = await fetchJson('/api/global-academic-years', { headers });
      const list = Array.isArray(years?.years) ? years.years : (Array.isArray(years) ? years : []);
      const current = list.find(y => y.isCurrent || String(y.status || '').toLowerCase() === 'current');
      if (current?.id) return current.id;
    } catch (_) {}
    // Last resort: return whatever is stored under school-local AY key
    const ayKeyName = schoolId ? `${schoolId}:currentAcademicYearId` : 'currentAcademicYearId';
    return localStorage.getItem(ayKeyName) || '';
  };

  // Auto-refresh AFTER fetchData is defined to avoid ReferenceError
  useScopeAutoRefresh([fetchData]);
  useEffect(() => { fetchData(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [activePhase]);

  // Fast prefetch of class-subject names to ensure chips render with real labels quickly
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const schoolId = localStorage.getItem('schoolId') || localStorage.getItem('activeSchoolId') || localStorage.getItem('scopeSchoolId') || '';
        const phaseCode = (activePhase || localStorage.getItem('schoolAdmin_activePhase') || localStorage.getItem('activePhase') || '');
        const ayId = await resolveCurrentAyId(schoolId, phaseCode);
        const phaseId = localStorage.getItem('schoolAdmin_activePhaseId') || localStorage.getItem('activePhaseId') || '';
        const headers = schoolId ? { 'X-Scope-School-Id': schoolId, 'X-Active-Phase': phaseCode, 'X-Academic-Year-Id': ayId, 'X-Active-Phase-Id': phaseId } : { 'X-Active-Phase': phaseCode, 'X-Academic-Year-Id': ayId, 'X-Active-Phase-Id': phaseId };
        const qBase = `${phaseCode ? `?phase=${encodeURIComponent(phaseCode)}` : ''}${(phaseCode ? '&' : '?')}schoolId=${encodeURIComponent(schoolId)}`;
        const q = `${qBase}&ayId=${encodeURIComponent(ayId)}&academicYearId=${encodeURIComponent(ayId)}&yearId=${encodeURIComponent(ayId)}`;
        let namesResp = null;
        try { namesResp = await fetchJson(`/api/allocations/class-subjects/names${q}`, { headers }); } catch(_) { namesResp = { map: {} }; }
        if (cancelled) return;
        const map = (namesResp && namesResp.map) ? namesResp.map : {};
        if (map && typeof map === 'object') {
          setSubjectsByClass(prev => ({ ...prev, ...map }));
          try { const namesCacheKey = `classAssignments:names:${schoolId}|${phaseCode || '-'}|${ayId || '-'}`; localStorage.setItem(namesCacheKey, JSON.stringify({ ...(JSON.parse(localStorage.getItem(namesCacheKey) || '{}')), ...map })); } catch(_) {}
          // Build/merge global id->name map
          try {
            const byId = { ...subjectNamesById };
            Object.values(map).forEach(arr => { (arr || []).forEach(x => { if (x && x.id) byId[String(x.id)] = x.name || x.code || byId[String(x.id)] || null; }); });
            setSubjectNamesById(byId);
          } catch(_) {}
          try { setNamesLoaded(true); } catch(_) {}
        }
      } catch (_) {}
    })();
    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activePhase]);

  // Deferred heavy lookups: class-subjects and teacher-subjects
  useEffect(() => {
    let cancelled = false;
    const loadHeavy = async () => {
      try {
        const schoolId = localStorage.getItem('schoolId') || localStorage.getItem('activeSchoolId') || localStorage.getItem('scopeSchoolId') || '';
        const phaseCode = (activePhase || localStorage.getItem('schoolAdmin_activePhase') || localStorage.getItem('activePhase') || '');
        // Resolve the Global Academic Year ID expected by backend middlewares so reads match writes
        const ayId = await resolveCurrentAyId(schoolId, phaseCode);
        const phaseId = localStorage.getItem('schoolAdmin_activePhaseId') || localStorage.getItem('activePhaseId') || '';
        const headers = schoolId ? { 'X-Scope-School-Id': schoolId, 'X-Active-Phase': phaseCode, 'X-Academic-Year-Id': ayId, 'X-Active-Phase-Id': phaseId } : { 'X-Active-Phase': phaseCode, 'X-Academic-Year-Id': ayId, 'X-Active-Phase-Id': phaseId };
        const qBase = `${phaseCode ? `?phase=${encodeURIComponent(phaseCode)}` : ''}${(phaseCode ? '&' : '?')}schoolId=${encodeURIComponent(schoolId)}`;
        const q = `${qBase}&ayId=${encodeURIComponent(ayId)}&academicYearId=${encodeURIComponent(ayId)}&yearId=${encodeURIComponent(ayId)}`;
        // Try to hydrate class-subjects from a small local cache so UI shows recent saves immediately after reload
        try {
          const csCacheKey = `classAssignments:cs:${schoolId}|${phaseCode || '-'}|${ayId || '-'}`;
          const csRawCache = localStorage.getItem(csCacheKey);
          if (csRawCache) {
            const csCached = JSON.parse(csRawCache);
            if (csCached && typeof csCached === 'object') setClassSubjects(csCached);
          }
          // Hydrate subjectsByClass from cache as well
          const namesCacheKey = `classAssignments:names:${schoolId}|${phaseCode || '-'}|${ayId || '-'}`;
          const namesRaw = localStorage.getItem(namesCacheKey);
          if (namesRaw) {
            const namesCached = JSON.parse(namesRaw);
            if (namesCached && typeof namesCached === 'object') setSubjectsByClass(namesCached);
          }
        } catch (_) {}
        const [cs, ts, subs, namesResp] = await Promise.all([
          // class-subjects with fallbacks (phase/AY scoped)
          (async () => { 
            try { return await fetchJson(`/api/class-subjects${q}`, { headers }); } 
            catch(_) { try { return await fetchJson(`/class-subjects${q}`, { headers }); } catch(_) { return []; } } 
          })(),
          // teacher-subjects with fallbacks (phase/AY scoped)
          (async () => { 
            try { return await fetchJson(`/api/teacher-subjects${q}`, { headers }); } 
            catch(_) { try { return await fetchJson(`/teacher-subjects${q}`, { headers }); } catch(_) { return []; } } 
          })(),
          // subjects list with fallbacks
          (async () => {
            // Ultra-fast allocations endpoint (server/index.js) — preferred
            try { return await fetchJson(`/api/allocations/subjects${q}`, { headers }); } catch (_) {}
            try { return await fetchJson('/api/allocations/subjects', { headers }); } catch (_) {}
            try { return await fetchJson(`/api/subjects/for-allocations${q}`, { headers }); } catch (_) {}
            try { return await fetchJson('/api/subjects/for-allocations', { headers }); } catch (_) {}
            try { return await fetchJson(`/api/subjects${q}`, { headers }); } catch (_) {}
            try { return await fetchJson('/api/subjects', { headers }); } catch (_) {}
            try { return await fetchJson(`/api/academics/subjects${q}`, { headers }); } catch (_) {}
            try { return await fetchJson('/api/academics/subjects', { headers }); } catch (_) {}
            try { return await fetchJson(`/academics/subjects${q}`, { headers }); } catch (_) {}
            try { return await fetchJson('/academics/subjects', { headers }); } catch (_) {}
            return [];
          })(),
          // server-joined names per class (authoritative labels)
          (async () => {
            try { return await fetchJson(`/api/allocations/class-subjects/names${q}`, { headers }); } catch (_) { return { map: {} }; }
          })()
        ]);
        if (cancelled) return;
        // capture raw load info for debugging
        try { setLastLoadLog({ q, headers, csRaw: cs, tsRaw: ts, subsRaw: subs }); } catch (_) {}
        // Normalize class-subjects to an array even if API returns a single object
        let csArray = Array.isArray(cs) ? cs : (cs?.classSubjects || cs?.data || []);
        if (!Array.isArray(csArray) && csArray && typeof csArray === 'object') {
          const looksLikeRow = (csArray.classId !== undefined) || (csArray.subjectId !== undefined);
          csArray = looksLikeRow ? [csArray] : [];
        }
        // If the bulk fetch returned nothing, fallback to fetching per-class to force DB reads on refresh
        if ((!csArray || csArray.length === 0) && Array.isArray(classes) && classes.length > 0) {
          try {
            const perClassResults = await Promise.all(
              classes.map(async (c) => {
                try {
                  const one = await fetchJson(`/api/class-subjects/class/${encodeURIComponent(c.id)}${q}`, { headers });
                  const arr = Array.isArray(one) ? one : (one?.classSubjects || one?.data || []);
                  if (!Array.isArray(arr)) return [];
                  return arr.map(it => ({
                    classId: c.id,
                    subjectId: it.subjectId ?? it.subjectID ?? it.subjectCode ?? it.code
                  }));
                } catch (_) {
                  return [];
                }
              })
            );
            csArray = perClassResults.flat();
          } catch (_) { /* ignore and keep empty */ }
        }
        // Ultimate fallback: try lite endpoint without strict filters to surface legacy rows
        if (!csArray || csArray.length === 0) {
          try {
            let lite = await fetchJson('/api/class-subjects/lite').catch(() => []);
            if (!Array.isArray(lite)) lite = (lite?.data || lite?.rows || lite?.classSubjects || []);
            if (Array.isArray(lite) && lite.length) {
              csArray = lite.map(it => ({ classId: it.classId, subjectId: it.subjectId ?? it.subjectID ?? it.subjectCode ?? it.code }));
            }
          } catch (_) {}
        }
        if (!csArray || csArray.length === 0) {
          try {
            let liteScoped = await fetchJson(`/api/class-subjects/lite${q}`, { headers }).catch(() => []);
            if (!Array.isArray(liteScoped)) liteScoped = (liteScoped?.data || liteScoped?.rows || liteScoped?.classSubjects || []);
            if (Array.isArray(liteScoped) && liteScoped.length) {
              csArray = liteScoped.map(it => ({ classId: it.classId, subjectId: it.subjectId ?? it.subjectID ?? it.subjectCode ?? it.code }));
            }
          } catch (_) {}
        }
        // Normalize teacher-subjects
        let tsArray = Array.isArray(ts) ? ts : (ts?.teacherSubjects || ts?.data || []);
        if (!Array.isArray(tsArray) && tsArray && typeof tsArray === 'object') {
          const looksLikeRow = (tsArray.teacherId !== undefined) || (tsArray.subjectId !== undefined);
          tsArray = looksLikeRow ? [tsArray] : [];
        }
        const subsArrayRaw = Array.isArray(subs) ? subs : (subs?.subjects || subs?.rows || subs?.data || []);
        // Normalize subjects
        const normSubs = (subsArrayRaw || []).map(s => ({
          id: s.id ?? s.subjectId ?? s.subjectID ?? s.code ?? s.subjectCode ?? s.shortName,
          name: s.name ?? s.title ?? s.subjectName ?? s.longName ?? s.code ?? s.subjectCode ?? t('classManagement.assignments.subjectFallback', { id: s.id ?? s.subjectId ?? '' }),
          code: s.code ?? s.subjectCode ?? s.shortName ?? '',
          // `level` MUST survive normalisation. The subject dialog filters on it
          // twice — strictly to the class's own level, and as a phase guard
          // against cross-phase levels — and dropping it here made both checks
          // no-ops, because `s.level` was undefined so every row passed. That is
          // why a primary class was offered first-cycle subjects. The other
          // normSubs, further down, already kept it; these two now agree.
          level: (s.level ?? s.classLevel ?? s.Level ?? s.levelName ?? null),
          phaseId: (s.phaseId ?? s.phase_id ?? null)
        })).filter(s => s.id != null);
        if (!normSubs || normSubs.length === 0) {
          console.warn('[ClassAssignments] Subjects endpoint returned empty. Tried with query:', q);
        }
        const csMap = {};
        csArray.forEach(item => {
          if (!csMap[item.classId]) csMap[item.classId] = [];
          csMap[item.classId].push(item.subjectId ?? item.subjectID ?? item.subjectCode ?? item.code);
        });
        const tsMap = {};
        tsArray.forEach(item => {
          if (!tsMap[item.teacherId]) tsMap[item.teacherId] = [];
          tsMap[item.teacherId].push(item.subjectId ?? item.subjectID ?? item.subjectCode ?? item.code);
        });
        // Only overwrite in-memory map if server returned data; otherwise keep cached map to prevent flicker to "No Subjects"
        if (Array.isArray(csArray) && csArray.length > 0) {
          setClassSubjects(csMap);
          // Persist to cache so Subjects table remains populated after refresh
          try {
            const csCacheKey = `classAssignments:cs:${schoolId}|${phaseCode || '-'}|${ayId || '-'}`;
            localStorage.setItem(csCacheKey, JSON.stringify(csMap));
          } catch (_) {}
        }
        setTeacherSubjects(tsMap);
        setSubjects(normSubs);
        // Names map from server (preferred for rendering). Only overwrite if server returns entries.
        try {
          const map = (namesResp && namesResp.map) ? namesResp.map : {};
          if (map && typeof map === 'object' && Object.keys(map).length > 0) {
            setSubjectsByClass(map);
            const namesCacheKey = `classAssignments:names:${schoolId}|${phaseCode || '-'}|${ayId || '-'}`;
            try { localStorage.setItem(namesCacheKey, JSON.stringify(map)); } catch(_) {}
            // Always merge IDs derived from the authoritative names map so partially-missing raw rows still render correctly
            const derived = {};
            Object.entries(map).forEach(([k, arr]) => {
              derived[k] = (Array.isArray(arr) ? arr : []).map(x => x.id);
            });
            setClassSubjects(prev => ({ ...prev, ...derived }));
            try {
              const csCacheKey = `classAssignments:cs:${schoolId}|${phaseCode || '-'}|${ayId || '-'}`;
              localStorage.setItem(csCacheKey, JSON.stringify({ ...(JSON.parse(localStorage.getItem(csCacheKey) || '{}')), ...derived }));
            } catch(_) {}
            // Build global id->name map for reliable label rendering
            try {
              const byId = {};
              Object.values(map).forEach(arr => {
                (arr || []).forEach(x => { if (x && x.id) byId[String(x.id)] = x.name || x.code || byId[String(x.id)] || null; });
              });
              setSubjectNamesById(byId);
            } catch(_) {}
          }
        } catch (_) {}
      } catch (_) {}
    };
    loadHeavy();
    // Respond to global phase/AY change events
    const onAy = () => { if (!cancelled) loadHeavy(); };
    const onPhase = () => { if (!cancelled) loadHeavy(); };
    try { window.addEventListener('app:ay:changed', onAy); } catch(_) {}
    try { window.addEventListener('app:phase:changed', onPhase); } catch(_) {}
    return () => {
      cancelled = true;
      try { window.removeEventListener('app:ay:changed', onAy); } catch(_) {}
      try { window.removeEventListener('app:phase:changed', onPhase); } catch(_) {}
    };
  }, [activePhase, classes.length]);

  // Helper to load subjects on-demand when dialog opens (if needed)
  const loadSubjects = async () => {
    try {
      setSubjectsLoading(true);
      const schoolId = localStorage.getItem('schoolId') || localStorage.getItem('activeSchoolId') || localStorage.getItem('scopeSchoolId') || '';
      const phaseCode = (activePhase || localStorage.getItem('schoolAdmin_activePhase') || localStorage.getItem('activePhase') || '');
      // Use Global Academic Year ID expected by backend (phase-aware)
      const ayId = await resolveCurrentAyId(schoolId, phaseCode);
      const headers = schoolId
        ? { 'X-Scope-School-Id': schoolId, 'X-Active-Phase': (phaseCode || '') }
        : { 'X-Active-Phase': (phaseCode || '') };
      const qBase = `${phaseCode ? `?phase=${encodeURIComponent(phaseCode)}` : ''}${(phaseCode ? '&' : '?')}schoolId=${encodeURIComponent(schoolId)}`;
      const q = `${qBase}&ayId=${encodeURIComponent(ayId)}&academicYearId=${encodeURIComponent(ayId)}&yearId=${encodeURIComponent(ayId)}`;
      let subs = [];
      const tryUrls = [
        `/api/allocations/subjects${q}`,
        '/api/allocations/subjects',
        `/api/subjects/for-allocations${q}`,
        '/api/subjects/for-allocations',
        `/api/subjects${q}`,
        '/api/subjects',
        `/api/academics/subjects${q}`,
        '/api/academics/subjects',
        `/academics/subjects${q}`,
        '/academics/subjects',
        '/api/subjects-lite',
        '/subjects',
      ];
      for (const url of tryUrls) {
        const ctrl = new AbortController();
        const timer = setTimeout(() => { try { ctrl.abort(); } catch(_){} }, 10000);
        try {
          const res = await fetchJson(url, { headers, signal: ctrl.signal });
          const arr = Array.isArray(res) ? res : (res?.subjects || res?.rows || res?.data || []);
          if (Array.isArray(arr) && arr.length) { subs = arr; break; }
        } catch (e) {
          // swallow and try next
        } finally {
          clearTimeout(timer);
        }
      }
      const normSubs = (subs || []).map(s => ({
        id: s.id ?? s.subjectId ?? s.subjectID ?? s.code ?? s.subjectCode ?? s.shortName,
        name: s.name ?? s.title ?? s.subjectName ?? s.longName ?? s.code ?? s.subjectCode ?? t('classManagement.assignments.subjectFallback', { id: s.id ?? s.subjectId ?? '' }),
        code: s.code ?? s.subjectCode ?? s.shortName ?? '',
        level: (s.level ?? s.classLevel ?? s.Level ?? s.levelName ?? null)
      })).filter(s => s.id != null);
      setSubjects(normSubs);
      if (!normSubs || normSubs.length === 0) {
        console.warn('[ClassAssignments] No subjects found after trying URLs:', tryUrls, 'with query:', q);
      }
    } finally {
      setSubjectsLoading(false);
    }
  };

  const handleEditAssignment = async (classItem) => {
    setEditingClass(classItem);
    setEditFormData({
      classMasterId: classItem.classMasterId || '',
      notes: classItem.assignmentNotes || ''
    });
    setEditDialogOpen(true);
    // Load server-assisted compatible teachers for this class (scoped by school/phase/AY)
    try {
      setCompatibleLoading(true);
      setCompatibleTeachers([]);
      const schoolId = localStorage.getItem('schoolId') || localStorage.getItem('activeSchoolId') || localStorage.getItem('scopeSchoolId') || '';
      const phaseCode = (activePhase || localStorage.getItem('schoolAdmin_activePhase') || localStorage.getItem('activePhase') || '');
      const ayId = await resolveCurrentAyId(schoolId, phaseCode);
      const phaseId = localStorage.getItem('schoolAdmin_activePhaseId') || localStorage.getItem('activePhaseId') || '';
      const headers = schoolId
        ? { 'X-Scope-School-Id': schoolId, 'X-Active-Phase': phaseCode, 'X-Academic-Year-Id': ayId, 'X-Active-Phase-Id': phaseId }
        : { 'X-Active-Phase': phaseCode, 'X-Academic-Year-Id': ayId, 'X-Active-Phase-Id': phaseId };
      const params = [];
      if (schoolId) params.push(`schoolId=${encodeURIComponent(schoolId)}`);
      if (phaseId) params.push(`phaseId=${encodeURIComponent(phaseId)}`);
      else if (phaseCode) params.push(`phase=${encodeURIComponent(phaseCode)}`);
      if (ayId) params.push(`ayId=${encodeURIComponent(ayId)}`);
      // Always request debug info from backend so we can inspect subjectIds/teacherIds when list is empty
      params.push('debug=1');
      const q = params.length ? `?${params.join('&')}` : '';
      const list = await fetchJson(`/api/classes/${classItem.id}/compatible-teachers${q}`, { headers }).catch(() => ({ teachers: [] }));
      const arr = Array.isArray(list) ? list : (list?.teachers || list?.data || []);
      const norm = (arr || []).map(t => ({
        id: t.id ?? t.userId ?? t.teacherId ?? t.accountId,
        firstName: t.firstName ?? t.givenName ?? (t.name?.split(' ')[0]) ?? '',
        lastName: t.lastName ?? t.surname ?? (t.name?.split(' ').slice(1).join(' ')) ?? '',
        email: t.email || ''
      })).filter(t => t.id != null);
      setCompatibleTeachers(norm);
    } finally {
      setCompatibleLoading(false);
    }
  };

  const handleSaveAssignment = async () => {
    // Treat empty selection as an explicit unassignment (set classMasterId to null)
    const newClassMasterId = editFormData.classMasterId ? editFormData.classMasterId : null;

    // Enforce uniqueness only when assigning a non-null class master
    if (newClassMasterId) {
      const conflict = classes.some(
        c => c.id !== editingClass.id && c.classMasterId && String(c.classMasterId) === String(newClassMasterId)
      );
      if (conflict) {
        enqueueSnackbar(assignmentConflictToast, { variant: 'warning' });
        return;
      }
    }

    try {
      setSaving(true);
      const schoolId = localStorage.getItem('schoolId') || localStorage.getItem('activeSchoolId') || localStorage.getItem('scopeSchoolId') || '';
      const phaseCode = (activePhase || localStorage.getItem('schoolAdmin_activePhase') || localStorage.getItem('activePhase') || '');
      // Resolve the Global Academic Year ID expected by backend middlewares (phase-aware)
      const ayId = await resolveCurrentAyId(schoolId, phaseCode);
      // Resolve phaseId (if persisted) so server receives explicit phase id
      const phaseId = localStorage.getItem('schoolAdmin_activePhaseId') || localStorage.getItem('activePhaseId') || '';
      const ctxHeaders = schoolId
        ? { 'X-Scope-School-Id': schoolId, 'X-Active-Phase': (phaseCode || ''), 'X-Academic-Year-Id': (ayId || ''), 'X-Active-Phase-Id': (phaseId || '') }
        : { 'X-Active-Phase': (phaseCode || ''), 'X-Academic-Year-Id': (ayId || ''), 'X-Active-Phase-Id': (phaseId || '') };

      // Only persist classMasterId on the Class record; keep notes/date as UI-only metadata
      const updateData = {
        classMasterId: newClassMasterId,
      };

      await fetchJson(`/api/classes/${editingClass.id}?academicYearId=${encodeURIComponent(ayId)}&phase=${encodeURIComponent(phaseCode)}&schoolId=${encodeURIComponent(schoolId)}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', ...ctxHeaders },
        body: JSON.stringify(updateData)
      });

      // Update local state so the table reflects the new assignment immediately
      const assignmentDateLocal = new Date().toISOString();
      const updatedClasses = classes.map(cls => 
        cls.id === editingClass.id 
          ? { 
              ...cls, 
              classMasterId: newClassMasterId,
              assignmentNotes: editFormData.notes,
              assignmentDate: assignmentDateLocal
            }
          : cls
      );
      setClasses(updatedClasses);

      enqueueSnackbar(assignmentSuccessToast, { variant: 'success' });
      setEditDialogOpen(false);
      setEditingClass(null);
      
    } catch (error) {
      console.error('Error updating assignment:', error);
      enqueueSnackbar(t('classManagement.assignments.toasts.assignment.error', { message: error?.message || '' }), { variant: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const handleCancelEdit = () => {
    setEditDialogOpen(false);
    setEditingClass(null);
    setEditFormData({ classMasterId: '', notes: '' });
  };

  const getTeacherName = (teacherId) => {
    if (!teacherId) return t('classManagement.assignments.labels.notAssigned');
    const allTeachers = Array.isArray(teachers) ? teachers : [];
    // Prefer global teachers list, then fall back to last loaded compatibleTeachers
    const teacher = allTeachers.find(t => String(t.id) === String(teacherId))
      || (Array.isArray(compatibleTeachers) ? compatibleTeachers.find(t => String(t.id) === String(teacherId)) : null);
    if (!teacher) return t('classManagement.assignments.labels.notAssigned');
    const first = teacher.firstName || teacher.givenName || (teacher.name ? String(teacher.name).split(' ')[0] : '');
    const last = teacher.lastName || teacher.surname || (teacher.name ? String(teacher.name).split(' ').slice(1).join(' ') : '');
    const full = `${first || ''} ${last || ''}`.trim() || teacher.name || teacher.email || t('classManagement.assignments.labels.notAssigned');
    return full;
  };

  // Filter teachers who can be ClassMaster of a specific class
  // Primary: teachers whose subject list intersects with the class's subjects (current phase)
  // Fallback: server-derived compatibleTeachers for the currently edited class
  const getCompatibleTeachers = (classId) => {
    if (!classId) return [];

    const teacherList = Array.isArray(teachers) ? teachers : [];
    const keyNum = classId;
    const keyStr = String(classId);

    // Resolve subject IDs for this class (prefer explicit IDs, then derive from names map)
    let classSubjIds = classSubjects[keyNum] || classSubjects[keyStr] || [];
    if ((!classSubjIds || classSubjIds.length === 0) && subjectsByClass && (subjectsByClass[keyStr] || subjectsByClass[keyNum])) {
      const arr = subjectsByClass[keyStr] || subjectsByClass[keyNum] || [];
      classSubjIds = (arr || []).map(x => x.id);
    }

    let localResult = [];
    if (
      teacherList.length > 0 &&
      Array.isArray(classSubjIds) &&
      classSubjIds.length > 0 &&
      Object.keys(teacherSubjects || {}).length > 0
    ) {
      const classSubjSet = new Set((classSubjIds || []).map(String));
      // A teacher is compatible if their subject list intersects with the class's subject list
      localResult = teacherList.filter(t => {
        const tKeyNum = t.id;
        const tKeyStr = String(t.id);
        const tSubjIds = teacherSubjects[tKeyNum] || teacherSubjects[tKeyStr] || [];
        if (!Array.isArray(tSubjIds) || tSubjIds.length === 0) return false;
        return tSubjIds.some(sid => classSubjSet.has(String(sid)));
      });
    }

    if (localResult.length > 0) return localResult;

    // Fallback: use server-derived list for the currently edited class, even if global teachers list is empty
    if (editingClass && editingClass.id === classId && Array.isArray(compatibleTeachers) && compatibleTeachers.length > 0) {
      return compatibleTeachers;
    }

    return [];
  };

  const mapsReady = (Object.keys(classSubjects || {}).length > 0) && (Object.keys(teacherSubjects || {}).length > 0);

  // Subjects editing
  const handleEditSubjects = (classItem) => {
    setEditingClass(classItem);
    const keyNum = classItem.id;
    const keyStr = String(classItem.id);
    let current = classSubjects[keyNum] || classSubjects[keyStr] || [];
    // If IDs map is missing, derive from server names map for this class
    if ((!current || current.length === 0) && subjectsByClass && (subjectsByClass[keyStr] || subjectsByClass[keyNum])) {
      const arr = subjectsByClass[keyStr] || subjectsByClass[keyNum] || [];
      current = (arr || []).map(x => x.id);
    }
    // Normalize to unique string IDs to match checkbox comparison
    const unique = Array.from(new Set((current || []).map(String)));
    setSubjectSelection(unique);
    setSubjError('');
    setEditSubjectsOpen(true);
    if (!subjects || subjects.length === 0) {
      loadSubjects();
    }
  };

  const handleToggleSubject = (subjectId) => {
    const sid = String(subjectId);
    setSubjectSelection(prev => prev.map(String).includes(sid) ? prev.filter(id => String(id) !== sid) : [...prev, subjectId]);
  };

  const handleSaveSubjects = async () => {
    if (!editingClass) return;
    // clear previous error and log entry
    setSubjError('');
    try {
      setSubjectsSaving(true);
      // Prepare context headers for enforceCurrentYear
      const schoolId = localStorage.getItem('schoolId') || localStorage.getItem('activeSchoolId') || localStorage.getItem('scopeSchoolId') || '';
      const phaseCode = (activePhase || localStorage.getItem('schoolAdmin_activePhase') || localStorage.getItem('activePhase') || '');
      // Resolve the Global Academic Year ID expected by backend middlewares (phase-aware)
      const ayId = await resolveCurrentAyId(schoolId, phaseCode);
      // Resolve phaseId (if persisted) so server receives explicit phase id
      const phaseId = localStorage.getItem('schoolAdmin_activePhaseId') || localStorage.getItem('activePhaseId') || '';
      const ctxHeaders = schoolId
        ? { 'X-Scope-School-Id': schoolId, 'X-Active-Phase': (phaseCode || ''), 'X-Academic-Year-Id': (ayId || ''), 'X-Active-Phase-Id': (phaseId || '') }
        : { 'X-Active-Phase': (phaseCode || ''), 'X-Academic-Year-Id': (ayId || ''), 'X-Active-Phase-Id': (phaseId || '') };

      // Replace assignments: DELETE then BULK CREATE with array payload
        const extractErrMsg = (err) => {
          try {
          if (!err) return t('common.errors.unknown');
          if (err instanceof Error) return err.message;
          if (typeof err === 'string') return err;
          // Try common response shapes
          if (err.status) return `HTTP ${err.status}`;
          if (err.message) return String(err.message);
          return JSON.stringify(err);
        } catch (_) { return t('classManagement.assignments.errors.unknown'); }
      };

      const doProtectedWrites = async () => {
        await fetchJson(`/api/class-subjects/class/${editingClass.id}?academicYearId=${encodeURIComponent(ayId)}&phase=${encodeURIComponent(phaseCode)}&schoolId=${encodeURIComponent(schoolId)}`, { method: 'DELETE', headers: ctxHeaders });
        const payload = (subjectSelection || []).map(sid => ({ classId: editingClass.id, subjectId: sid }));
        if (payload.length > 0) {
          const bodyPayload = payload.map(p => ({ ...p, academicYearId: ayId, phaseId: phaseId ? Number(phaseId) : undefined }));
          await fetchJson(`/api/class-subjects/bulk?academicYearId=${encodeURIComponent(ayId)}&phase=${encodeURIComponent(phaseCode)}&schoolId=${encodeURIComponent(schoolId)}`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', ...ctxHeaders },
            body: JSON.stringify(bodyPayload)
          });
        }
      };
      // The /api/allocations/class-subjects/* bypass that used to live here has
      // been removed along with the retry that called it.
      //
      // A refusal is surfaced, never worked around.
      //
      // This used to retry through /api/allocations/class-subjects/* whenever
      // the guarded route answered 403 or "read-only" — that is, exactly when
      // the guard was doing its job. Those routes ran on `auth` alone, so the
      // fallback let anyone with an account rewrite a class's subjects and let
      // writes land in a closed academic year. They now carry the same guards,
      // so the fallback can no longer succeed anyway; retrying would only turn
      // one clear refusal into two.
      //
      // 403 means the user lacks /class-management. "Past academic years are
      // read-only" means the year switcher is on a finished session. Both are
      // answers, and both are shown.
      try {
        await doProtectedWrites();
      } catch (err) {
        throw new Error(extractErrMsg(err));
      }
      // Update local map
      // After a successful write, perform a read from server for this class to ensure UI reflects persisted rows
      try {
        const schoolId = localStorage.getItem('schoolId') || localStorage.getItem('activeSchoolId') || localStorage.getItem('scopeSchoolId') || '';
        const phaseCode = (activePhase || localStorage.getItem('schoolAdmin_activePhase') || localStorage.getItem('activePhase') || '');
        const ayId = await resolveCurrentAyId(schoolId, phaseCode);
        const q = `?academicYearId=${encodeURIComponent(ayId)}&phase=${encodeURIComponent(phaseCode)}&schoolId=${encodeURIComponent(schoolId)}`;
        const fetched = await fetchJson(`/api/class-subjects/class/${editingClass.id}${q}`, { headers: ctxHeaders }).catch(err => { console.warn('[ClassAssignments] post-save refetch failed', err); return null; });
        let fetchedArray = Array.isArray(fetched) ? fetched : (fetched?.classSubjects || fetched?.data || []);
        if (!Array.isArray(fetchedArray) && fetchedArray && typeof fetchedArray === 'object') {
          const looksLikeRow = (fetchedArray.classId !== undefined) || (fetchedArray.subjectId !== undefined);
          fetchedArray = looksLikeRow ? [fetchedArray] : [];
        }
        const savedIds = (fetchedArray || []).map(it => it.subjectId ?? it.subjectID ?? it.subjectCode ?? it.code).filter(Boolean);
        setClassSubjects(prev => ({ ...prev, [editingClass.id]: savedIds.length ? savedIds : [...subjectSelection] }));
        // Also refresh server-joined names for this class for authoritative labels
        try {
          const namesUrl = `/api/allocations/class-subjects/names?academicYearId=${encodeURIComponent(ayId)}&phase=${encodeURIComponent(phaseCode)}&schoolId=${encodeURIComponent(schoolId)}`;
          const names = await fetchJson(namesUrl, { headers: ctxHeaders }).catch(() => ({ map: {} }));
          const map = names && names.map ? names.map : {};
          if (map && map[editingClass.id]) {
            setSubjectsByClass(prev => ({ ...prev, [editingClass.id]: map[editingClass.id] }));
          }
        } catch (_) {}
        try {
          setLastSaveLog({
            time: new Date().toISOString(),
            classId: editingClass.id,
            phaseCode,
            ayId,
            ctxHeaders,
            attempted: [...subjectSelection],
            serverReturned: fetchedArray
          });
        } catch (_) {}
        // Clear cached list for this AY/phase so next load fetches fresh data
        try {
          const cacheKey = `classAssignments:list:${schoolId}|${phaseCode || '-'}|${ayId || '-'}`;
          localStorage.removeItem(cacheKey);
          // Also persist the small cs map for immediate reload visibility
          try {
            const csCacheKey = `classAssignments:cs:${schoolId}|${phaseCode || '-'}|${ayId || '-'}`;
            const newMap = Object.assign({}, (JSON.parse(localStorage.getItem(csCacheKey) || '{}')));
            newMap[editingClass.id] = savedIds.length ? savedIds : [...subjectSelection];
            localStorage.setItem(csCacheKey, JSON.stringify(newMap));
            pushDebugTimeline({ stage: 'postSave:writeCsCache', csCacheKey, classId: editingClass.id, savedCount: (newMap[editingClass.id] || []).length });
          } catch(_) {}
        } catch(_) {}
      } catch (e) {
        console.warn('[ClassAssignments] failed to refresh class-subjects after save:', e);
        setClassSubjects(prev => ({ ...prev, [editingClass.id]: [...subjectSelection] }));
      }
      setEditSubjectsOpen(false);
      setEditingClass(null);
      enqueueSnackbar(t('classManagement.assignments.toasts.subjects.success'), { variant: 'success' });
    } catch (e) {
      setSubjError(e?.message || t('classManagement.assignments.errors.updateSubjects'));
    }
    finally {
      setSubjectsSaving(false);
    }
  };

  // Do not block whole page on loading; render content and allow inline updates

  return (
    <Box sx={{ p: { xs: 2, sm: 3 } }}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/class-management')}
          sx={{ mr: 2 }}
        >
          {t('classManagement.assignments.back')}
        </Button>
        <Box>
          <Typography variant="h4" component="h1">
            {t('classManagement.assignments.pageTitle')}
          </Typography>
          <Typography variant="body1" color="textSecondary">
            {assignmentsSubtitle}
          </Typography>
        </Box>
      </Box>

      {/* Error Display */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* Summary Cards */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} md={3}>
          <Card>
            <CardContent>
              <Typography variant="h4" color="primary">
                {classes.length}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                {t('classManagement.assignments.summaryCards.totalClasses')}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={3}>
          <Card>
            <CardContent>
              <Typography variant="h4" color="success.main">
                {classes.filter(c => c.classMasterId).length}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                {t('classManagement.assignments.summaryCards.assignedClasses')}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={3}>
          <Card>
            <CardContent>
              <Typography variant="h4" color="warning.main">
                {classes.filter(c => !c.classMasterId).length}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                {t('classManagement.assignments.summaryCards.unassignedClasses')}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={3}>
          <Card>
            <CardContent>
              <Typography variant="h4" color="info.main">
                {classes.filter(c => {
                  const compatibleTeachers = getCompatibleTeachers(c.id);
                  return compatibleTeachers.length > 0;
                }).length}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                {t('classManagement.assignments.summaryCards.withCompatible')}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Tabs for functionalities */}
      <Paper sx={{ p: 0, mb: 2 }}>
        <Tabs value={activeTab} onChange={(_, v) => setActiveTab(v)} variant="scrollable" scrollButtons="auto" allowScrollButtonsMobile>
          <Tab label={t('classManagement.assignments.tabs.subjects')} />
          <Tab label={classMastersTabLabel} />
          <Tab label={t('classManagement.assignments.tabs.electives')} sx={{ display: 'none' }} />
          <Tab label={t('classManagement.assignments.tabs.teacherSubjects')} />
        </Tabs>
      </Paper>

      {/* Tab: Electives (now rendered under tab controls) */}
      {activeTab === 2 && false && (
        <Paper sx={{ p: 3 }}>
          <Typography variant="h6" gutterBottom>
            {t('classManagement.assignments.electives.title')}
          </Typography>
          <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid item xs={12} md={4}>
              <FormControl fullWidth>
                <InputLabel>{t('classManagement.assignments.electives.fields.class')}</InputLabel>
                <Select label={t('classManagement.assignments.electives.fields.class')} value={electClassId} onChange={(e) => setElectClassId(e.target.value)}>
                  <MenuItem value=""><em>{t('classManagement.assignments.electives.fields.selectClassPlaceholder')}</em></MenuItem>
                  {classes.map(c => (
                    <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>
          {electClassId && (
            <Grid container spacing={2}>
              <Grid item xs={12} md={4}>
                <Paper variant="outlined" sx={{ p: 1, maxHeight: 420, overflow: 'auto' }}>
                  <List dense>
                    {electStudents.map(s => (
                      <ListItem key={s.id} button selected={String(electStudentId) === String(s.id)} onClick={() => setElectStudentId(String(s.id))}>
                        <ListItemText primary={`${s.firstName || ''} ${s.lastName || ''}`} secondary={s.studentNumber || ''} />
                      </ListItem>
                    ))}
                    {(!electStudents || electStudents.length === 0) && (
                      <ListItem><ListItemText primary={t('classManagement.assignments.electives.students.noStudents')} /></ListItem>
                    )}
                  </List>
                </Paper>
              </Grid>
              <Grid item xs={12} md={8}>
                <Paper variant="outlined" sx={{ p: 2, maxHeight: 420, overflow: 'auto', opacity: electLoading ? 0.6 : 1 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mb: 1 }}>
                    <Button size="small" variant="outlined" onClick={() => loadElectives(electClassId)}>{t('classManagement.assignments.electives.buttons.refresh')}</Button>
                    <Button size="small" variant="contained" onClick={saveElectivesForStudent} disabled={!electStudentId}>{t('classManagement.assignments.electives.buttons.save')}</Button>
                  </Box>
                  {(!electSubjects || electSubjects.length === 0) && (
                    <Alert severity="info">{t('classManagement.assignments.electives.alerts.noOfferedSubjects')}</Alert>
                  )}
                  {electSubjects.map(s => (
                    <ListItem key={s.id} disableGutters secondaryAction={null}>
                      <ListItemIcon>
                        <Checkbox edge="start" disabled={!electStudentId} checked={(electMap[String(electStudentId)] || []).map(String).includes(String(s.id))} onChange={(e) => toggleElective(String(electStudentId), s.id, e.target.checked)} />
                      </ListItemIcon>
                      <ListItemText primary={s.name} secondary={s.code || ''} />
                    </ListItem>
                  ))}
                </Paper>
              </Grid>
            </Grid>
          )}
        </Paper>
      )}

      {/* Tab: Subjects (now first) */}
      {activeTab === 0 && (
        <Paper sx={{ p: 3 }}>
          <Typography variant="h6" gutterBottom>
            {t('classManagement.assignments.subjectsTab.title')}
          </Typography>
          <TableContainer sx={{ overflowX: 'auto', width: '100%' }}>
            <Table sx={{ minWidth: 600 }}>
              <TableHead>
                <TableRow>
                  <TableCell>{t('classManagement.assignments.subjectsTab.table.headers.class')}</TableCell>
                  <TableCell>{t('classManagement.assignments.subjectsTab.table.headers.subjects')}</TableCell>
                  <TableCell>{t('classManagement.assignments.subjectsTab.table.headers.actions')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {classes.map((c) => {
                  const subjIds = classSubjects[c.id] || classSubjects[String(c.id)] || [];
                  return (
                    <TableRow key={c.id}>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <ClassIcon sx={{ mr: 1, color: 'primary.main' }} />
                          {c.name}
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                          {(() => { /* normalize names map key */ })()}
                          {/* Removed Loading chip to avoid mixed labels */}
                          {namesLoaded && (!(subjectsByClass[String(c.id)] && subjectsByClass[String(c.id)].length > 0) && !(subjectsByClass[c.id] && subjectsByClass[c.id].length > 0)) && subjIds.length === 0 && (
                            <Chip label={t('classManagement.assignments.subjectsTab.table.noSubjects')} size="small" />
                          )}
                          {(
                            ((subjectsByClass[String(c.id)] && subjectsByClass[String(c.id)].length > 0) || (subjectsByClass[c.id] && subjectsByClass[c.id].length > 0))
                              ? (subjectsByClass[String(c.id)] || subjectsByClass[c.id])
                              : (namesLoaded
                                  ? subjIds.map(id => {
                                      // Prefer names from server-provided map; fallback to subjects list; then generic label
                                      const fromNames = ((subjectsByClass[String(c.id)] || subjectsByClass[c.id] || [])).find(x => String(x.id) === String(id));
                                      const s = subjects.find(x => String(x.id) === String(id));
                                      const label = fromNames?.name || subjectNamesById[String(id)] || (s ? (s.name || s.title || s.code) : null) || t('classManagement.assignments.subjectFallback', { id });
                                      return { id, name: label };
                                    })
                                  : []
                                )
                          ).map(s => (
                            <Chip key={s.id} label={s.name} size="small" />
                          ))}
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Button startIcon={<EditIcon />} variant="outlined" size="small" onClick={() => handleEditSubjects(c)}>{t('classManagement.assignments.subjectsTab.actions.assignSubjects')}</Button>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}

      {/* Tab: Class Masters (now second) */}
      {activeTab === 1 && (
        <Paper sx={{ p: 3 }}>
          <Typography variant="h6" gutterBottom>
            {classMastersTabLabel}
          </Typography>
          <TableContainer sx={{ overflowX: 'auto', width: '100%' }}>
            <Table sx={{ minWidth: 720 }}>
              <TableHead>
                <TableRow>
                  <TableCell>{t('classManagement.assignments.classMastersTab.table.headers.class')}</TableCell>
                  <TableCell>{t('classManagement.assignments.classMastersTab.table.headers.academicYear')}</TableCell>
                  <TableCell>{currentClassMasterLabel}</TableCell>
                  <TableCell>{t('classManagement.assignments.classMastersTab.table.headers.assignmentDate')}</TableCell>
                  <TableCell>{t('classManagement.assignments.classMastersTab.table.headers.actions')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {classes.map((classItem) => (
                  <TableRow key={classItem.id}>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <ClassIcon sx={{ mr: 1, color: 'primary.main' }} />
                        {classItem.name}
                      </Box>
                    </TableCell>
                    <TableCell>{classItem.academicYear}</TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        {(() => {
                          const teacher = (teachers || []).find(t => String(t.id) === String(classItem.classMasterId));
                          const displayName = getTeacherName(classItem.classMasterId);
                          if (classItem.classMasterId && teacher) {
                            return (
                              <>
                                <Avatar
                                  sx={{ width: 28, height: 28, mr: 1, bgcolor: 'primary.main', fontSize: 14 }}
                                  src={teacher.avatarUrl || undefined}
                                >
                                  {(displayName || '?').charAt(0).toUpperCase()}
                                </Avatar>
                                <Typography variant="body2" noWrap sx={{ maxWidth: 220 }}>
                                  {displayName}
                                </Typography>
                              </>
                            );
                          }
                          return (
                            <>
                              <PersonIcon sx={{ mr: 1, color: 'text.secondary' }} />
                              <Typography variant="body2" noWrap sx={{ maxWidth: 220 }}>
                                {displayName}
                              </Typography>
                            </>
                          );
                        })()}
                      </Box>
                    </TableCell>
                    <TableCell>
                      {classItem.assignmentDate ? new Date(classItem.assignmentDate).toLocaleDateString() : '-'}
                    </TableCell>
                    <TableCell>
                      <Button startIcon={<EditIcon />} onClick={() => handleEditAssignment(classItem)} variant="outlined" size="small">
                        {assignClassMasterLabel}
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}

      {activeTab === 3 && (
        <Paper sx={{ p: 3 }}>
          <Typography variant="h6" gutterBottom>
            {t('classManagement.assignments.teacherSubjectsTab.title')}
          </Typography>
          {!mapsReady && (
            <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
              {t('classManagement.assignments.teacherSubjectsTab.loading')}
            </Typography>
          )}
          <TableContainer sx={{ overflowX: 'auto', width: '100%' }}>
            <Table sx={{ minWidth: 560 }}>
              <TableHead>
                <TableRow>
                  <TableCell>{t('classManagement.assignments.teacherSubjectsTab.table.headers.teacher')}</TableCell>
                  <TableCell>{t('classManagement.assignments.teacherSubjectsTab.table.headers.subjects')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {(() => {
                  const classTeacherIdSet = (() => {
                    try {
                      if (!isNurPriPhase) return new Set();
                      const ids = (classes || [])
                        .map((c) => c && (c.classMasterId ?? c.classTeacherId ?? c.class_master_id))
                        .filter((v) => v !== null && v !== undefined && String(v).trim() !== '')
                        .map((v) => String(v));
                      return new Set(ids);
                    } catch (_) {
                      return new Set();
                    }
                  })();

                  const teacherRows = (teachers || []).filter((teacher) => {
                    const rawRole = teacher && (teacher.role || teacher.roleName || (teacher.Role && teacher.Role.name));
                    if (!rawRole) return false;
                    const norm = String(rawRole).trim().toLowerCase();
                    return norm === 'teacher';
                  }).filter((teacher) => {
                    if (!isNurPriPhase) return true;
                    return !classTeacherIdSet.has(String(teacher?.id));
                  });

                  if (!teacherRows || teacherRows.length === 0) {
                    return (
                      <TableRow>
                        <TableCell colSpan={2} align="center">
                          <Typography variant="body2" color="textSecondary">
                            {t('classManagement.assignments.teacherSubjectsTab.table.noTeachers')}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    );
                  }

                  return teacherRows.map((teacher) => {
                    const keyNum = teacher.id;
                    const keyStr = String(teacher.id);
                    const subjIds = teacherSubjects[keyNum] || teacherSubjects[keyStr] || [];
                    const first = teacher.firstName || teacher.givenName || (teacher.name ? String(teacher.name).split(' ')[0] : '');
                    const last = teacher.lastName || teacher.surname || (teacher.name ? String(teacher.name).split(' ').slice(1).join(' ') : '');
                    const displayName = (`${first || ''} ${last || ''}`.trim()) || teacher.name || teacher.email || String(teacher.id);
                    const subjectsForTeacher = (subjIds || []).map(id => {
                      const idStr = String(id);
                      const fromMap = subjectNamesById[idStr];
                      if (fromMap) {
                        return { id: idStr, name: fromMap };
                      }
                      const fromList = (subjects || []).find(s => String(s.id) === idStr);
                      const label = fromList?.name || fromList?.title || fromList?.code || t('classManagement.assignments.subjectFallback', { id: idStr });
                      return { id: idStr, name: label };
                    });
                    return (
                      <TableRow key={teacher.id}>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <Avatar
                              sx={{ width: 28, height: 28, mr: 1, bgcolor: 'primary.main', fontSize: 14 }}
                              src={teacher.avatarUrl || undefined}
                            >
                              {(displayName || '?').charAt(0).toUpperCase()}
                            </Avatar>
                            <Typography variant="body2" noWrap sx={{ maxWidth: 220 }}>
                              {displayName}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                            {subjectsForTeacher.length === 0 ? (
                              <Chip label={t('classManagement.assignments.teacherSubjectsTab.table.noSubjects')} size="small" />
                            ) : (
                              subjectsForTeacher.map(s => (
                                <Chip key={s.id} label={s.name} size="small" />
                              ))
                            )}
                          </Box>
                        </TableCell>
                      </TableRow>
                    );
                  });
                })()}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}

      {/* Edit Assignment Dialog */}
      <Dialog open={editDialogOpen} onClose={handleCancelEdit} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editAssignmentTitle}
          {editingClass && (
            <Typography variant="body2" color="textSecondary">
              {editingClass.name}
            </Typography>
          )}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <Alert severity="info" sx={{ mb: 2 }}>
                {editAssignmentInfo}
              </Alert>
              <FormControl fullWidth>
                <InputLabel>{classMasterLabel}</InputLabel>
                <Select
                  value={editFormData.classMasterId}
                  onChange={(e) => setEditFormData(prev => ({ ...prev, classMasterId: e.target.value }))}
                  label={classMasterLabel}
                >
                  <MenuItem value="">
                    <em>{selectClassMasterLabel}</em>
                  </MenuItem>
                  {compatibleLoading && (
                    <MenuItem disabled value="__loading">
                      <em>{t('classManagement.assignments.dialogs.editAssignment.options.loadingTeachers')}</em>
                    </MenuItem>
                  )}
                  {!compatibleLoading && getCompatibleTeachers(editingClass?.id).length === 0 && (
                    <MenuItem disabled value="__none">
                      <em>{t('classManagement.assignments.dialogs.editAssignment.options.noCompatibleTeachers')}</em>
                    </MenuItem>
                  )}
                  {!compatibleLoading && getCompatibleTeachers(editingClass?.id).map((teacher) => {
                    const first = teacher.firstName || teacher.givenName || (teacher.name ? String(teacher.name).split(' ')[0] : '');
                    const last = teacher.lastName || teacher.surname || (teacher.name ? String(teacher.name).split(' ').slice(1).join(' ') : '');
                    const baseLabel = (`${first || ''} ${last || ''}`.trim()) || teacher.name || teacher.email || String(teacher.id);
                    const isAlreadyClassMaster = classes.some(c => String(c.classMasterId) === String(teacher.id));
                    const isOtherClassMaster = isAlreadyClassMaster && editingClass && String(editingClass.classMasterId) !== String(teacher.id);
                    const label = isOtherClassMaster ? `${baseLabel} ${classMasterSuffixLabel}` : baseLabel;
                    return (
                      <MenuItem
                        key={teacher.id}
                        value={teacher.id}
                        disabled={isOtherClassMaster}
                      >
                        {label}
                      </MenuItem>
                    );
                  })}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label={t('classManagement.assignments.dialogs.editAssignment.fields.notes')}
                value={editFormData.notes}
                onChange={(e) => setEditFormData(prev => ({ ...prev, notes: e.target.value }))}
                placeholder={t('classManagement.assignments.dialogs.editAssignment.fields.notesPlaceholder')}
                multiline
                rows={3}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelEdit} disabled={saving}>
            {t('classManagement.assignments.dialogs.editAssignment.buttons.cancel')}
          </Button>
          <Button
            onClick={handleSaveAssignment}
            variant="contained"
            startIcon={saving ? <CircularProgress size={20} /> : <SaveIcon />}
            disabled={saving}
          >
            {saving ? t('classManagement.assignments.dialogs.editAssignment.buttons.saving') : t('classManagement.assignments.dialogs.editAssignment.buttons.save')}
          </Button>
        </DialogActions>
      </Dialog>
     
      {/* Edit Subjects Dialog */}
      <Dialog open={editSubjectsOpen} onClose={() => setEditSubjectsOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          {t('classManagement.assignments.dialogs.editSubjects.title')}
          {editingClass && (
            <Typography variant="body2" color="textSecondary">
              {editingClass.name}
            </Typography>
          )}
        </DialogTitle>
        <DialogContent>
          {subjError && <Alert severity="error" sx={{ mb: 2 }}>{subjError}</Alert>}
          <TextField
            fullWidth
            placeholder={t('classManagement.assignments.dialogs.editSubjects.searchPlaceholder')}
            value={subjectSearch}
            onChange={(e) => setSubjectSearch(e.target.value)}
            sx={{ mb: 2 }}
          />
          {subjectsLoading && (
            <Alert severity="info" sx={{ mb: 2 }}>{t('classManagement.assignments.dialogs.editSubjects.alerts.loadingSubjects')}</Alert>
          )}
          {(!subjectsLoading && (!subjects || subjects.length === 0)) && (
            <Alert severity="info" sx={{ mb: 2 }}>
              {t('classManagement.assignments.dialogs.editSubjects.alerts.noSubjectsFound')}
            </Alert>
          )}
          <List dense sx={{ maxHeight: 360, overflow: 'auto', border: theme => `1px solid ${theme.palette.divider}`, borderRadius: 1, opacity: (subjects || []).length ? 1 : 0.6 }}>
            {(subjects || []).filter(s => {
              const classLevel = (editingClass?.level || '').toString().trim().toLowerCase();
              const subjLevel = (s?.level || '').toString().trim().toLowerCase();
              // Strict level filter: if the class has a level, only show subjects that match it
              if (classLevel && subjLevel && subjLevel !== classLevel) return false;
              // Phase guard: block cross-phase subjects even if level is missing
              const nurPriLevels = ['nursery', 'primary'];
              const secLevels = ['first_cycle', 'second_cycle'];
              if (subjLevel) {
                if (resolvedPhaseCode === 'nur_pri' && secLevels.includes(subjLevel)) return false;
                if (resolvedPhaseCode === 'sec' && nurPriLevels.includes(subjLevel)) return false;
              }
              const q = subjectSearch.trim().toLowerCase();
              if (!q) return true;
              const name = (s.name || s.title || '').toLowerCase();
              const code = (s.code || '').toLowerCase();
              return name.includes(q) || code.includes(q);
            }).map(s => (
              <ListItem key={String(s.id)} button onClick={() => handleToggleSubject(s.id)}>
                <ListItemIcon>
                  <Checkbox edge="start" checked={subjectSelection.map(String).includes(String(s.id))} tabIndex={-1} disableRipple />
                </ListItemIcon>
                <ListItemText primary={s.name || s.title} secondary={s.code || ''} />
              </ListItem>
            ))}
          </List>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditSubjectsOpen(false)}>{t('classManagement.assignments.dialogs.editSubjects.buttons.cancel')}</Button>
          <Button onClick={handleSaveSubjects} variant="contained" startIcon={<SaveIcon />}>{t('classManagement.assignments.dialogs.editSubjects.buttons.save')}</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ClassAssignmentsPage;
